import { Grid, CardHeader, CardContent, Box, TextField, CircularProgress, Select, MenuItem, InputLabel, FormControl, Button, Paper, Typography, Stack, Autocomplete, ImageList, ImageListItem } from "@mui/material";
import React, { useState, useRef, useEffect , useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Form, FormikProvider, useFormik } from "formik";
import errorHandler from "../../utils/errorHandler";
import { IMAGE_BASE } from "../../utils/constants";
import LoadingButton from "@mui/lab/LoadingButton";
import CardLayout from "../../layouts/CardLayout";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { bannerAndOffer } from "../../utils/Schema";
import FieldErrorMessage from "../../components/FieldErrorMessage";
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from "dayjs";
import { MobileDateTimePicker } from "@mui/x-date-pickers";
import { apiOfferBannerManagement } from "../../services/AllServices";
import { capitalizeFirstLetter, trimFormValues } from "../../utils/helpers";

function AddbannerOffer() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [data , setData] = useState();
  const [auctionList, setAuctionList] = useState([]);
  const [vehicleImages, setVehicleImages] = useState([]);
  const [selectedVehicleImageIndex, setSelectedVehicleImageIndex] = useState(null);
  const [selectedVehicleImagePath, setSelectedVehicleImagePath] = useState(null);

  const { id } = useParams();
  const location = useLocation();
  const { auctionId } = location.state || {};
  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await apiOfferBannerManagement.getById({ id });
        const resData = response?.data?.data;
        setImagePreview(IMAGE_BASE + resData?.bannerImage)
       
        setData(resData)
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);    

  // Fetch auction list
  const fetchAuctionList = useCallback(async () => {
    try {
      const payload = {
        "pageNumber":1,
        "pageSize":1000
    }
      const response = await apiOfferBannerManagement.auctionList(payload);
      setAuctionList(response?.data?.data?.auctions || []);
    } catch (error) {
      errorHandler(error);
    }
  }, []);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  const formik = useFormik({
    enableReinitialize: true,      
    initialValues: {
      name               : data?.title ? data?.title : "",
      description        : data?.description || "",
      startDate          : data?.visibilityPeriod?.startDate ? data?.visibilityPeriod?.startDate : "",
      imagePath          : "banner",
      endDate            : data?.visibilityPeriod?.endDate ? data?.visibilityPeriod?.endDate : "",
      image              : data?.bannerImage ? IMAGE_BASE + data?.bannerImage : "",
      auctionId          : data?.auctionId ? data?.auctionId?._id : "",
      bannerType         : data?.bannerType || (auctionId ? "promotional" : "normal"),
    },
    validationSchema: bannerAndOffer(id),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const formData = new FormData();
        
        if(id){
          formData.append("id", id);
        }

         const trimmedValues = trimFormValues(values);
         
         formData.append("title", trimmedValues.name);
         formData.append("description", trimmedValues.description);
         formData.append("startDate", trimmedValues.startDate);
         formData.append("endDate", trimmedValues.endDate);
         formData.append("bannerType", trimmedValues.bannerType);
         
        if (trimmedValues.bannerType === "promotional") {
          formData.append("auctionId", trimmedValues.auctionId || auctionId);
        }

        // Send vehicle image path if selected, otherwise send manual file
        if (selectedVehicleImagePath) {
          formData.append("bannerImage", selectedVehicleImagePath);
        } else if (selectedImage) {
          formData.append("bannerImage", selectedImage);
        }
        const response = id
        ? await apiOfferBannerManagement.edit(formData)
        : await apiOfferBannerManagement.add(formData);

        toast.success(response?.data?.message, { toastId: "success" });
        navigate(-1);
        setLoading(false);
    
        fileInputRef.current.value = "";
        setSelectedImage(null);
        setSelectedVehicleImagePath(null);
        setSelectedVehicleImageIndex(null);
        setImagePreview(null);
      } catch (error) {
        console.log("err",error);
        errorHandler(error);
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (formik.values.bannerType === "promotional" || data?.bannerType === "promotional" || auctionId) {
      fetchAuctionList();
    }
  }, [formik.values.bannerType, data?.bannerType, auctionId, fetchAuctionList]);

  useEffect(() => {
    if (auctionId && auctionList.length > 0 && formik.values.auctionId === "") {
      formik.setFieldValue('auctionId', auctionId);
      formik.setFieldTouched('auctionId', true);
    }
  }, [auctionId, auctionList, formik]);

  // Auto-fill name and description when promotional banner and auction is selected
  useEffect(() => {
    // Only auto-fill when creating new banner (not editing) or when auctionId changes
    if (formik.values.bannerType === "promotional" && formik.values.auctionId && auctionList.length > 0 && !id) {
      const selectedAuction = auctionList.find(auction => auction._id === formik.values.auctionId);
      
      if (selectedAuction) {
        // Auto-fill name: make + model
        if (selectedAuction.vehicle) {
          const year = selectedAuction.vehicle.year || '';
          const make = selectedAuction.vehicle.make || '';
          const model = selectedAuction.vehicle.model || '';
          const name = `${year} ${make} ${model}`.trim();
          if (name) {
            formik.setFieldValue('name', name);
          }
        }
        
        // Auto-fill description: auction description
        if (selectedAuction.description) {
          formik.setFieldValue('description', selectedAuction.description);
        }
      }
    }
  }, [formik.values.bannerType, formik.values.auctionId, auctionList, id]);

  // Extract vehicle images when auction is selected
  useEffect(() => {
    if (formik.values.bannerType === "promotional" && formik.values.auctionId && auctionList.length > 0) {
      const selectedAuction = auctionList.find(auction => auction._id === formik.values.auctionId);
      if (selectedAuction?.vehicle?.vehicleImage && Array.isArray(selectedAuction.vehicle.vehicleImage)) {
        setVehicleImages(selectedAuction.vehicle.vehicleImage);
      } else {
        setVehicleImages([]);
      }
      // Reset selected vehicle image when auction changes
      setSelectedVehicleImageIndex(null);
      setSelectedVehicleImagePath(null);
    } else {
      setVehicleImages([]);
      setSelectedVehicleImageIndex(null);
      setSelectedVehicleImagePath(null);
    }
  }, [formik.values.bannerType, formik.values.auctionId, auctionList]);


  const handleImageChange = (event) => {
    const file = event.currentTarget.files[0];
    const validTypes = ["image/jpg", "image/png", "image/jpeg"];

    if (selectedImage && selectedImage.name === file?.name) {
      toast.error("You cannot upload the same image again.");
      fileInputRef.current.value = "";
      return;
    }

    if (file && validTypes.includes(file.type)) {
      setImageLoading(true);
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
      formik.setFieldValue("image", file);
      // Clear vehicle image selection when uploading new file
      setSelectedVehicleImageIndex(null);
      setSelectedVehicleImagePath(null);
      setTimeout(() => {
        setImageLoading(false);
      }, 2000);
    } else {
      toast.error("Only png, jpg, jpeg allowed.).");
      fileInputRef.current.value = "";
    }
  };

  // Handle vehicle image selection
  const handleVehicleImageSelect = (imagePath, index) => {
    // Store the image path directly (as it comes from API)
    setSelectedVehicleImagePath(imagePath);
    setSelectedVehicleImageIndex(index);
    
    // Set preview
    const fullImagePath = imagePath.startsWith('http') ? imagePath : IMAGE_BASE + imagePath;
    setImagePreview(fullImagePath);
    
    // Clear manual file selection
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    
    // Set form value (using path as string)
    formik.setFieldValue("image", imagePath);
  };

  const isToday = (date) => dayjs().isSame(date, 'day');
  const getMinDateTime = () => {
    const now = dayjs();
    // Return current time + 1 minute to ensure it's strictly after current time
    return now.add(1, 'minute');
  };

  const getMinEndDateTime = () => {
    const now = dayjs();
    const startDate = formik.values.startDate ? dayjs(formik.values.startDate) : null;
    
    // End date must always be strictly after current time
    const minFromCurrentTime = now.add(1, 'minute');
    
    // If start date is selected and is in the future, end date must also be after start date
    if (startDate && startDate.isAfter(now)) {
      const minFromStartDate = startDate.add(1, 'minute');
      // Return the later of the two constraints (current time or start date)
      return minFromStartDate.isAfter(minFromCurrentTime) ? minFromStartDate : minFromCurrentTime;
    }
    
    // Otherwise, end date must be strictly after current time
    return minFromCurrentTime;
  };

  return (
    <Box>
      <Helmet>
        <title>{id ? 'Edit' : 'Add'} Offer and Banner</title>
      </Helmet>
      <CardLayout>
        <CardHeader title={`${id ? 'Edit' : 'Add'} Offer and Banner`} />
        <CardContent className="banner-form-content">
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
              <Grid container spacing={4} className="banner-form-grid">
                {/* Image Upload Section */}
                <Grid item xs={12} className="banner-form-grid-item">
                  <Paper 
                    elevation={0} 
                    className="banner-upload-paper"
                  >
                    <input
                      accept="image/*"
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageChange}
                      className="banner-file-input"
                    />

                    <Stack spacing={3} alignItems="center">
                      <Typography variant="h6" color="text.secondary" textAlign="center" className="banner-section-title">
                        Banner Image
                      </Typography>
                      <Box
                        onClick={() => fileInputRef.current.click()}
                        className="banner-image-preview-container"
                      >
                        {imageLoading ? (
                          <Box 
                            display="flex" 
                            alignItems="center" 
                            justifyContent="center"
                            className="banner-image-loading-box"
                          >
                            <CircularProgress size={40} className="banner-loading-spinner" />
                          </Box>
                        ) : (
                          <Box className="banner-image-preview-box">
                            {imagePreview ? (
                              <img
                                src={imagePreview}
                                alt="Banner preview"
                                className="banner-image-preview"
                              />
                            ) : (
                              <Stack alignItems="center" spacing={1}>
                                <Box className="banner-image-icon-box">
                                  <Typography variant="h4" color="text.secondary">
                                    📷
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  Click to upload image
                                </Typography>
                              </Stack>
                            )}
                          </Box>
                        )}
                      </Box>

                      {/* Upload Button */}
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => fileInputRef.current.click()}
                        className="banner-upload-button"
                      >
                        {id ? "Edit" : imagePreview ? "Change" : "Upload"} Image
                      </Button>

                      {/* Error Message */}
                      {formik.touched.image && formik.errors.image && (
                        <Typography 
                          variant="body2" 
                          color="error" 
                          textAlign="center"
                          className="banner-error-message"
                        >
                          {formik.errors.image}
                        </Typography>
                      )}

                      {/* Vehicle Images Gallery - Show when promotional banner and auction is selected */}
                      {formik.values.bannerType === "promotional" && formik.values.auctionId && vehicleImages.length > 0 && (
                        <Box sx={{ width: "100%", mt: 2 }}>
                          <Typography variant="subtitle1" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
                            Or select from vehicle images:
                          </Typography>
                          <ImageList sx={{ width: "100%", height: 200 }} cols={4} rowHeight={150}>
                            {vehicleImages.map((image, index) => (
                              <ImageListItem 
                                key={index}
                                onClick={() => handleVehicleImageSelect(image, index)}
                                sx={{
                                  cursor: "pointer",
                                  border: selectedVehicleImageIndex === index ? "3px solid #1976d2" : "2px solid #e0e0e0",
                                  borderRadius: 1,
                                  overflow: "hidden",
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    border: "3px solid #1976d2",
                                    transform: "scale(1.05)",
                                  },
                                }}
                              >
                                <Box
                                  component="img"
                                  src={IMAGE_BASE + image}
                                  alt={`Vehicle image ${index + 1}`}
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                  loading="lazy"
                                />
                              </ImageListItem>
                            ))}
                          </ImageList>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                </Grid>

                 {/* Banner Type Selection */}
                 <Grid item xs={12} className="banner-form-grid-item">
                  <Paper elevation={0} className="banner-section-paper">
                    <Typography variant="h6" className="banner-section-heading">
                      Banner Type
                    </Typography>
                    
                    <Stack spacing={3}>
                      <FormControl 
                        fullWidth 
                        required
                        error={Boolean(formik.touched.bannerType && formik.errors.bannerType)}
                        disabled={!!auctionId} 
                      >
                        <InputLabel id="banner-type-label">Banner Type</InputLabel>
                        <Select
                          labelId="banner-type-label"
                          label="Banner Type"
                          value={formik.values.bannerType}
                          onChange={(e) => {
                            formik.handleChange(e);
                            // Clear auctionId when switching to normal
                            if (e.target.value === "normal") {
                              formik.setFieldValue('auctionId', '');
                              setVehicleImages([]);
                              setSelectedVehicleImageIndex(null);
                              setSelectedVehicleImagePath(null);
                              // Clear image preview and selected image if it was from vehicle
                              if (selectedVehicleImageIndex !== null || selectedVehicleImagePath) {
                                setImagePreview(null);
                                setSelectedImage(null);
                                formik.setFieldValue("image", "");
                              }
                            }
                            if (e.target.value === "promotional") {
                              fetchAuctionList();
                            }
                          }}
                          name="bannerType"
                          className="banner-select-field"
                        >
                          <MenuItem value="normal">Normal</MenuItem>
                          <MenuItem value="promotional">Promotional</MenuItem>
                        </Select>
                        {auctionId && (
                          <Typography variant="caption" color="text.secondary" className="banner-caption-text">
                            Banner type is automatically set to "Promotional" when created from auction management
                          </Typography>
                        )}
                      </FormControl>
                      <FieldErrorMessage formik={formik} name="bannerType" />
                    </Stack>
                  </Paper>
                </Grid>

                  {/* Auction Selection Section - Only show for promotional banners */}
                  {formik.values.bannerType === "promotional" && (
                  <Grid item xs={12} className="banner-form-grid-item">
                    <Paper elevation={0} className="banner-section-paper">
                      <Typography variant="h6" className="banner-section-heading">
                        Auction Selection
                      </Typography>
                    
                    <Stack spacing={3}>
                      {auctionId ? (
                        // Show read-only field when auctionId is provided
                        <TextField
                          fullWidth
                          label="Selected Auction"
                          value={auctionList.find(auction => auction._id == auctionId)?.vehicle?.vehicleName || 'Loading...'}
                          disabled
                          className="banner-auction-disabled-field"
                          helperText="Auction pre-selected from promotional banner action"
                        />
                      ) : (
                        // Show normal dropdown when no auctionId is provided
                        <Autocomplete
                          options={auctionList}
                          getOptionLabel={(option) => 
                            option?.vehicle 
                              ? `${option.vehicle?.year || ''} ${capitalizeFirstLetter(option.vehicle?.make || '')} ${capitalizeFirstLetter(option.vehicle?.model || '')}`.trim()
                              : ''
                          }
                          value={auctionList.find(auction => auction._id === formik.values.auctionId) || null}
                          onChange={(e, newValue) => {
                            formik.setFieldValue("auctionId", newValue?._id || "");
                            
                            // Auto-fill name and description when auction is selected
                            if (newValue && formik.values.bannerType === "promotional") {
                              // Auto-fill name: make + model
                              if (newValue.vehicle) {
                                const year = newValue.vehicle.year || '';
                                const make = newValue.vehicle.make || '';
                                const model = newValue.vehicle.model || '';
                                const name = `${year} ${make} ${model}`.trim();
                                if (name) {
                                  formik.setFieldValue('name', name);
                                }
                              }
                              
                              // Auto-fill description: auction description
                              if (newValue.description) {
                                formik.setFieldValue('description', newValue.description);
                              }
                            } else {
                              // Clear name and description if auction is deselected
                              if (!newValue) {
                                formik.setFieldValue('name', '');
                                formik.setFieldValue('description', '');
                                setVehicleImages([]);
                                setSelectedVehicleImageIndex(null);
                                setSelectedVehicleImagePath(null);
                                // Clear image preview and selected image if it was from vehicle
                                if (selectedVehicleImageIndex !== null || selectedVehicleImagePath) {
                                  setImagePreview(null);
                                  setSelectedImage(null);
                                  formik.setFieldValue("image", "");
                                }
                              }
                            }
                          }}
                          isOptionEqualToValue={(option, value) => option._id === value?._id}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select Auction"
                              required
                              placeholder="Search auction..."
                              error={Boolean(formik.touched.auctionId && formik.errors.auctionId)}
                              className="banner-autocomplete-field"
                            />
                          )}
                          noOptionsText={auctionList.length === 0 ? "No Upcoming and Live Auction Available" : "No options found"}
                          className="banner-autocomplete-field"
                        />
                      )}
                      <FieldErrorMessage formik={formik} name="auctionId" />
                    </Stack>
                  </Paper>
                </Grid>
                )}
                
                {/* Basic Information Section */}
                <Grid item xs={12} className="banner-form-grid-item">
                  <Paper elevation={0} className="banner-section-paper">
                    <Typography variant="h6" className="banner-section-heading">
                      Basic Information
                    </Typography>
                    
                    <Stack spacing={3}>
                      <TextField
                        fullWidth
                        label="Name"
                        // required
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        name="name"
                        error={Boolean(formik.touched.name && formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                        className="banner-text-field"
                      />
                    
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        name="description"
                        label="Description"
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        // required
                        error={Boolean(formik.touched.description && formik.errors.description)}
                        helperText={formik.touched.description && formik.errors.description}
                        className="banner-text-field"
                      />
                    </Stack>
                  </Paper>
                </Grid>

                {/* Date & Time Section */}
                <Grid item xs={12} className="banner-form-grid-item">
                  <Paper elevation={0} className="banner-section-paper">
                    <Typography variant="h6" className="banner-section-heading">
                      Schedule
                    </Typography>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DemoContainer components={['DateTimePicker']}>
                            <MobileDateTimePicker 
                              label="Start Date"
                              name="startDate"
                              value={formik.values.startDate ? dayjs(formik.values.startDate) : null}
                              onChange={(event) => {
                                if (event && event.$d) {
                                  const value = event.$d;
                                  const date = new Date(value);
                                  // Check if date is valid
                                  if (!isNaN(date.getTime())) {
                                    formik.setFieldValue("startDate", date.toISOString());
                                    // If end date exists and is before new start date, trigger validation
                                    if (formik.values.endDate) {
                                      formik.setFieldTouched("endDate", true);
                                    }
                                  }
                                }
                              }}
                              helperText={formik.touched.startDate && formik.errors.startDate}
                              minDateTime={getMinDateTime()}
                              className="banner-date-picker"
                              disabled={data?.auctionId?.auctionStatus !== "Upcoming" && id}
                            />
                          </DemoContainer>
                        </LocalizationProvider>
                        <FieldErrorMessage formik={formik} name="startDate" />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DemoContainer components={['DateTimePicker']}>
                            <MobileDateTimePicker 
                              label="End Date"
                              name="endDate"
                              value={formik?.values?.endDate ? dayjs(formik?.values?.endDate) : null}
                              onChange={(event) => {
                                if (event && event.$d) {
                                  const value = event.$d;
                                  const date = new Date(value);
                                  // Check if date is valid
                                  if (!isNaN(date.getTime())) {
                                    formik.setFieldValue("endDate", date.toISOString());
                                    // Trigger validation for endDate when it changes
                                    formik.setFieldTouched("endDate", true);
                                  }
                                }
                              }}
                              error={Boolean(formik.touched.endDate && formik.errors.endDate)}
                              helperText={formik.touched.endDate && formik.errors.endDate}
                              minDateTime={getMinEndDateTime()}
                              className="banner-date-picker"
                            />
                          </DemoContainer>
                        </LocalizationProvider>
                        <FieldErrorMessage formik={formik} name="endDate" />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                
              

                {/* Submit Button */}
                <Grid item xs={12} className="banner-form-grid-item">
                  <Paper elevation={0} className="banner-submit-paper">
                    <Box display="flex" justifyContent="center">
                      <LoadingButton 
                        size="large" 
                        type="submit" 
                        variant="contained" 
                        loading={loading}
                        className="banner-submit-button"
                      >
                        {id ? 'Update' : 'Create'} {formik.values.offerType === "Offer" ? "Offer" : "Banner"}
                      </LoadingButton>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddbannerOffer;